import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import AnnouncementBanner from "./AnnouncementBanner";
import NotificationBell from "./NotificationBell";
import OnboardingTour from "./OnboardingTour";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  LayoutDashboard,
  Phone,
  Users,
  Target,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Activity,
  BarChart3,
  Trophy,
  ShieldOff,
  User,
  UsersRound,
  Gauge,
  ScrollText,
  Megaphone,
  Coins,
  CreditCard,
  Headset,
  ClipboardList,
  MessageSquare,
  Zap,
  PhoneCall,
} from "lucide-react";
import { cn } from "../lib/utils";

const LOGO_URL = "https://digitalmastermedia.com/logo192.png";
const COMPANY_NAME = "Callintell";

function getNavSections(role) {
  const isAdmin = role === "admin";
  
  const sections = [
    {
      title: "Core",
      items: [
        { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
        { name: "Analytics", path: "/analytics", icon: BarChart3 },
      ],
    },
    {
      title: "Publisher Control",
      items: [
        { name: "Publisher Report", path: "/publisher-report", icon: Users },
        ...(isAdmin ? [{ name: "Publisher Activity", path: "/publisher-activity", icon: Activity }] : []),
        { name: "Publisher Ranking", path: "/publisher-ranking", icon: Trophy },
        { name: "DID Status", path: "/did-status", icon: PhoneCall },
        ...(isAdmin ? [{ name: "Publisher Exclusions", path: "/publisher-exclusions", icon: ShieldOff }] : []),
      ],
    },
    {
      title: "Call Intelligence",
      items: [
        { name: "Call Analysis", path: "/call-analysis", icon: Phone },
        { name: "Action Center", path: "/action-center", icon: Target },
      ],
    },
    {
      title: "Revenue Intelligence",
      items: [
        { name: "Buyer Optimizer", path: "/buyer-optimizer", icon: Coins },
      ],
    },
    {
      title: "Account",
      items: [
        { name: "Profile", path: "/profile", icon: User },
        { name: "Activity Log", path: "/activity-log", icon: ClipboardList },
        ...(isAdmin ? [
          { name: "Software Guide", path: "/software-guide", icon: ScrollText },
          { name: "Audit Log", path: "/audit-log", icon: ScrollText },
          { name: "Settings", path: "/settings", icon: Settings },
        ] : []),
      ],
    },
  ];
  
  return sections;
}

export function Layout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navSections = getNavSections(user?.role);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/60 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="text-slate-400 hover:text-white hover:bg-slate-800/50"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <img src={LOGO_URL} alt="Callintell" className="h-7 w-7 rounded-lg overflow-hidden object-cover ring-1 ring-white/10" style={{ mixBlendMode: 'screen', backgroundColor: '#0f172a' }} />
        </div>
        <div className="flex items-center gap-1.5">
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-mobile"
            className="text-slate-400 hover:text-white"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 h-full bg-[#0B0D12] border-r border-white/5 transition-all duration-300 flex flex-col",
          sidebarCollapsed ? "w-[68px]" : "w-60",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn("h-14 flex items-center border-b border-white/5", sidebarCollapsed ? "justify-center px-2" : "justify-between px-4")}>
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500/15 to-cyan-500/10 rounded-xl blur-[3px]" />
                <div className="relative h-8 w-8 rounded-xl overflow-hidden ring-1 ring-white/10 shadow-md shadow-black/30 bg-slate-900">
                  <img src={LOGO_URL} alt="DMM" className="h-full w-full object-cover" style={{ mixBlendMode: 'screen' }} />
                </div>
              </div>
              <div className="min-w-0">
                <span className="text-xs font-bold text-slate-100 tracking-wide truncate block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {COMPANY_NAME}<span className="text-blue-400">™</span>
                </span>
                <span className="text-[8px] text-slate-500 tracking-[0.12em] uppercase font-medium">Digital Master Media</span>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500/15 to-cyan-500/10 rounded-xl blur-[3px]" />
              <div className="relative h-7 w-7 rounded-xl overflow-hidden ring-1 ring-white/10 shadow-md shadow-black/30 bg-slate-900">
                <img src={LOGO_URL} alt="DMM" className="h-full w-full object-cover" style={{ mixBlendMode: 'screen' }} />
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex h-7 w-7 text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2.5 py-3 overflow-y-auto sidebar-scroll">
          {navSections.map((section, sIdx) => (
            <div key={section.title} className={cn(sIdx > 0 && "mt-5")}>
              {!sidebarCollapsed && (
                <div className="px-3 mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500/80">
                    {section.title}
                  </span>
                </div>
              )}
              {sidebarCollapsed && sIdx > 0 && (
                <div className="mx-3 mb-2 border-t border-white/5" />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={sidebarCollapsed ? item.name : undefined}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group relative",
                        isActive
                          ? "bg-blue-600/15 text-blue-400"
                          : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-blue-500 rounded-r-full" />
                      )}
                      <item.icon className={cn("h-[18px] w-[18px] flex-shrink-0", isActive && "text-blue-400")} />
                      {!sidebarCollapsed && (
                        <span className="text-[13px] font-medium">{item.name}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-white/5">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5 px-2">
              <div className="pulse-dot" />
              <span className="text-[11px] text-slate-500 font-medium">System Online</span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="flex justify-center">
              <div className="pulse-dot" />
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn("transition-all duration-300", sidebarCollapsed ? "lg:ml-[68px]" : "lg:ml-60")}>
        {/* Header */}
        <header className="hidden lg:flex sticky top-0 z-30 h-14 bg-[#0B0D12]/90 backdrop-blur-xl border-b border-white/5 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-blue-500/10">
              <ShieldAlert className="h-4 w-4 text-blue-400" />
            </div>
            <h1 className="text-sm font-semibold text-slate-300" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Callintell™ AI Call Intelligence
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle"
              className="h-8 w-8 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-8 px-2 hover:bg-slate-800/50" data-testid="user-menu">
                  <Avatar className="h-7 w-7">
                    {user?.logo_url && <AvatarImage src={user.logo_url} alt={user?.name || "User"} className="object-cover" />}
                    <AvatarFallback className="bg-blue-600/20 text-blue-400 text-xs font-semibold">
                      {user?.name?.charAt(0) || "A"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-slate-300">{user?.name || "Admin"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#11141D] border-white/10">
                <DropdownMenuItem className="text-xs text-slate-400 focus:bg-white/5">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/5" />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-400 focus:bg-white/5 focus:text-red-400">
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="px-3 py-4 pt-18 sm:p-5 lg:pt-5 min-h-[calc(100vh-3.5rem)]">
          <AnnouncementBanner />
          {children}
        </main>
      </div>

      {/* Onboarding Tour */}
      <OnboardingTour />
    </div>
  );
}
